import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

import { AuthService } from '../auth/auth.service';
import { SessionExpiryWarningService } from '../auth/session-expiry-warning.service';
import { SessionService } from '../auth/session.service';
import { TokenResponse } from '../auth/auth.models';

const PUBLIC_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/register-company',
  '/webhooks/stripe',
  '/webhooks/paypal'
] as const;

let refreshInFlight$: Observable<TokenResponse> | null = null;
const CSRF_COOKIE_NAME = 'badge_csrf_token';

function isPublicRequest(url: string): boolean {
  return PUBLIC_PATHS.some((p) => url.includes(p));
}

function hasCookie(name: string): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const encodedName = `${encodeURIComponent(name)}=`;
  return document.cookie
    .split(';')
    .some((part) => part.trim().startsWith(encodedName));
}

function getRefreshFlow(auth: AuthService, session: SessionService): Observable<TokenResponse> {
  if (refreshInFlight$) {
    return refreshInFlight$;
  }

  refreshInFlight$ = auth.refresh().pipe(
    switchMap((tokens) => [tokens]),
    finalize(() => {
      refreshInFlight$ = null;
    }),
    shareReplay(1)
  );

  return refreshInFlight$;
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.location.replace('/login');
}

function finalizeSession(
  auth: AuthService,
  session: SessionService,
  sessionExpiry: SessionExpiryWarningService,
  context: Record<string, unknown>,
): Observable<null> {
  debugLog('finalizeSession -> auth.logout', context);
  return auth.logout().pipe(
    catchError(() => of(null)),
    switchMap(() => {
      sessionExpiry.stop();
      session.clearSession();
      redirectToLogin();
      return of(null);
    })
  );
}

export const refreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const session = inject(SessionService);
  const sessionExpiry = inject(SessionExpiryWarningService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (isPublicRequest(req.url)) {
        return throwError(() => error);
      }

      if (!hasCookie(CSRF_COOKIE_NAME)) {
        debugLog('401 without CSRF cookie -> local clearSession + redirect', {
          request_url: req.url,
          request_method: req.method,
        });
        sessionExpiry.stop();
        session.clearSession();
        redirectToLogin();
        return throwError(() => error);
      }

      const retriedReq = req.clone({ withCredentials: true });
      return getRefreshFlow(auth, session).pipe(
        tap((tokens) => {
          session.setTokenPair(tokens);
          sessionExpiry.onTokenPairUpdated();
        }),
        catchError((refreshError) => {
          return finalizeSession(auth, session, sessionExpiry, {
            request_url: req.url,
            request_method: req.method,
            refresh_error_status: refreshError instanceof HttpErrorResponse ? refreshError.status : null,
          }).pipe(
            switchMap(() => throwError(() => refreshError))
          );
        }),
        switchMap(() => next(retriedReq))
      );
    })
  );
};

function debugLog(message: string, payload?: unknown): void {
  if (environment.production) {
    return;
  }
  console.warn(`[refreshTokenInterceptor] ${message}`, payload ?? null);
}
